import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { applyNodeChanges, type NodeChange, type OnSelectionChangeParams } from '@xyflow/react';
import {
  IconButton,
  ICON_BUTTON_SIZE_TB,
  ModeBadge,
  MODE_BADGE_SIZE_SM,
  MODE_BADGE_VARIANT_FLAT,
  Spinner,
  Toolbar,
  TOOLBAR_DENSITY_COMPACT,
  TOOLBAR_VARIANT_FLOATING,
  TOOLTIP_PLACEMENT_BOTTOM,
} from '@aibuild/ui';
import {
  cancelRun,
  getRun,
  isTerminalNodeStatus,
  isTerminalRunStatus,
  type RunStatus,
} from '@api/processRuns';
import useSnackbar from '@hooks/useSnackbar';
import { EditorContextProvider } from '../editor/EditorContext';
import GraphCanvas from '../editor/GraphCanvas';
import type { ProcessNode, ProcessEdge } from '../editor/graphTypes';
import ExecutionLog from '../editor/dock/ExecutionLog';
import NodeDetailPanel from '../editor/dock/NodeDetailPanel';
import { useNodeTranscript } from '../editor/dock/useNodeTranscript';
import { latestStateByNode } from '../editor/executionOrder';
import { ToolbarWrapper } from '../editor/canvasToolbar/CanvasToolbar.styled';
import NavLegend from '../editor/canvasToolbar/NavLegend';
import MaterialIcon from '../editor/canvasToolbar/MaterialIcon';
import {
  processRunDetailQueryKey,
  processRunsListQueryKey,
} from '../hooks/processRunsQueryKeys';
import { CanvasArea, EmptyState } from '../ProcessEditorPage.styled';
import { buildRunDetailGraph } from './buildRunDetailGraph';
import { useRunDetailContext } from './useRunDetailContext';
import { useRunDetailLive } from './useRunDetailLive';
import {
  FloatingMeta,
  FloatingStatusDot,
  FloatingStatusLabel,
  RunDockBody,
  RunDockRoot,
} from './ProcessRunDetailPage.styled';

const STATUS_COLOR: Record<RunStatus, string> = {
  completed: '#4ade80',
  failed: '#f87171',
  cancelled: 'var(--on-surface-variant, #94a3b8)',
  running: 'color-mix(in oklch, var(--primary, #C1C1FF) 90%, white)',
  pending: 'var(--on-surface-variant, #94a3b8)',
  waiting_for_action: 'var(--on-surface-variant, #94a3b8)',
};

const STATUS_LABEL: Record<RunStatus, string> = {
  completed: 'Succeeded',
  failed: 'Failed',
  cancelled: 'Cancelled',
  running: 'In progress',
  pending: 'Pending',
  waiting_for_action: 'Waiting',
};

const MODE_BADGE_TONE: Record<RunStatus, 'success' | 'error' | 'neutral' | 'primary'> = {
  completed: 'success',
  failed: 'error',
  cancelled: 'neutral',
  running: 'primary',
  pending: 'neutral',
  waiting_for_action: 'neutral',
};

const NOOP_EDGES_CHANGE = () => {};
const NOOP_CONNECT = () => {};
const NO_TRANSCRIPT: readonly never[] = [];
/** Fallback for a stream that cannot connect; the stream itself drives refreshes. */
const IN_FLIGHT_POLL_MS = 10_000;

const formatDuration = (ms: number | null): string => {
  if (ms == null) return '';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}m ${r}s`;
};

export interface RunDetailViewProps {
  readonly processId: string;
  readonly runId: string;
}

/**
 * Renders one run's snapshot graph + execution log inside the existing
 * editor page chrome. Swapped in by {@code ProcessEditorPage} when a
 * {@code runId} route param is present — the surrounding {@code PageRoot},
 * {@code ProcessEditorTopBar}, and sidebar all stay as-is; only the canvas
 * area and right dock are replaced. A run still in flight is followed live
 * and can be stopped from here, since the editor only ever holds the newest.
 */
const RunDetailView: React.FC<RunDetailViewProps> = ({
  processId,
  runId,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showError } = useSnackbar();
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);

  const { data: detail, isLoading, isError } = useQuery({
    queryKey: processRunDetailQueryKey(processId, runId),
    queryFn: () => getRun(processId, runId),
    enabled: !!processId && !!runId,
    staleTime: Infinity,
    refetchInterval: (query) =>
      query.state.data && !isTerminalRunStatus(query.state.data.run.status)
        ? IN_FLIGHT_POLL_MS
        : false,
  });

  const inFlight = !!detail && !isTerminalRunStatus(detail.run.status);
  const livePerNode = useRunDetailLive(processId, runId, inFlight);

  const stopRun = useMutation({
    mutationFn: () => cancelRun(processId, runId),
    onSuccess: () => {
      queryClient
        .invalidateQueries({ queryKey: processRunDetailQueryKey(processId, runId) })
        .catch(() => undefined);
      queryClient
        .invalidateQueries({ queryKey: processRunsListQueryKey(processId) })
        .catch(() => undefined);
    },
    onError: () => showError('Failed to cancel run. Please try again.'),
  });

  const latestStates = useMemo(() => latestStateByNode(detail?.nodeStates ?? []), [detail]);
  const selectedLive = selectedNodeId ? livePerNode.get(selectedNodeId) : undefined;
  const selectedRow = selectedNodeId ? latestStates.get(selectedNodeId) : undefined;
  // The stream's word on a node holds only while the run is in flight: a dropped
  // final envelope must not keep a finished node looking unsettled.
  const selectedState = inFlight ? selectedLive : selectedRow;
  // One source for both halves of the failure copy. Falling back per-field
  // would caption this run's message with a previous run's detail, and a live
  // null means "no error on this run" rather than "look in the row".
  const selectedFailure = selectedState ?? selectedRow;
  const selectedStatus = selectedState?.status ?? selectedRow?.status;
  const selectedSettled = selectedStatus === 'completed' || selectedStatus === 'failed';
  const selectedWorking =
    inFlight &&
    selectedStatus !== undefined &&
    selectedStatus !== 'pending' &&
    !isTerminalNodeStatus(selectedStatus);
  const rowTranscript = useNodeTranscript(
    processId,
    runId,
    selectedNodeId ?? undefined,
    selectedState?.iteration ?? selectedRow?.iteration ?? 0,
    selectedSettled,
  );
  // The row's transcript is complete once the node settles; until then only the stream has one.
  const selectedTranscript =
    (selectedSettled ? rowTranscript : undefined) ?? selectedLive?.transcript ?? NO_TRANSCRIPT;

  const onSelectionChange = useCallback(({ nodes: picked }: OnSelectionChangeParams) => {
    const firstId = picked[0]?.id ?? null;
    setSelectedNodeId((curr) => (curr === firstId ? curr : firstId));
  }, []);

  const runDetail = useMemo(
    () => (detail ? buildRunDetailGraph(detail) : null),
    [detail],
  );

  const editorCtx = useRunDetailContext(
    processId,
    runDetail?.runStateMap ?? new Map(),
    inFlight,
  );

  const snapshotNodes = useMemo(() => runDetail?.nodes ?? [], [runDetail]);
  const edges = useMemo<ProcessEdge[]>(() => runDetail?.edges ?? [], [runDetail]);

  const [nodes, setNodes] = useState<ProcessNode[]>(snapshotNodes);
  const prevSnapshotRef = useRef(snapshotNodes);
  if (prevSnapshotRef.current !== snapshotNodes) {
    prevSnapshotRef.current = snapshotNodes;
    setNodes(snapshotNodes);
  }

  const onNodesChange = useCallback(
    (changes: NodeChange<ProcessNode>[]) => {
      setNodes((prev) => applyNodeChanges(changes, prev));
    },
    [],
  );

  const goBack = useCallback(() => {
    navigate(`/processes/${processId}`);
  }, [navigate, processId]);

  if (isError) {
    return (
      <>
        <CanvasArea $dockCollapsed={false}>
          <EmptyState data-testid="run-detail__error">
            Failed to load run details.
          </EmptyState>
        </CanvasArea>
        <RunDockRoot>
          <RunDockBody />
        </RunDockRoot>
      </>
    );
  }

  if (isLoading || !runDetail || !detail) {
    return (
      <>
        <CanvasArea $dockCollapsed={false}>
          <EmptyState data-testid="run-detail__loading">
            <Spinner size="lg" />
          </EmptyState>
        </CanvasArea>
        <RunDockRoot data-testid="run-detail__dock-loading">
          <RunDockBody />
        </RunDockRoot>
      </>
    );
  }

  const { run } = detail;
  const color = STATUS_COLOR[run.status];
  const durationMs =
    runDetail.startedAtMs && runDetail.finishedAtMs
      ? runDetail.finishedAtMs - runDetail.startedAtMs
      : null;

  return (
    <>
      <CanvasArea $dockCollapsed={false} data-testid="run-detail__canvas-area">
        <EditorContextProvider value={editorCtx}>
          <GraphCanvas
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={NOOP_EDGES_CHANGE}
            onConnect={NOOP_CONNECT}
            onSelectionChange={onSelectionChange}
            readOnly
            dataTestId="run-detail__canvas"
            overlays={
              <RunDetailOverlays
                runNumber={run.runNumber}
                status={run.status}
                color={color}
                durationMs={durationMs}
                startedBy={run.startedBy.name}
                onBack={goBack}
                onStop={inFlight ? () => stopRun.mutate() : undefined}
                isStopping={stopRun.isPending}
              />
            }
          />
        </EditorContextProvider>
      </CanvasArea>
      <RunDockRoot data-testid="run-detail__dock">
        <RunDockBody>
          {selectedNodeId ? (
            <NodeDetailPanel
              errorMessage={selectedFailure?.errorMessage ?? null}
              errorDetail={selectedFailure?.errorDetail ?? null}
              transcript={selectedTranscript}
              isLive={selectedWorking}
              dataTestId="run-detail__node-detail"
            />
          ) : (
            <ExecutionLog
              phase={runDetail.phase}
              total={runDetail.executionSteps.length}
              order={runDetail.executionSteps}
              currentId={null}
              startedAt={runDetail.startedAtMs}
              finishedAt={runDetail.finishedAtMs}
              runNumber={run.runNumber}
              runOutcome={runDetail.runOutcome ?? undefined}
              startedBy={run.startedBy.name}
              dataTestId="run-detail__execution-log"
            />
          )}
        </RunDockBody>
      </RunDockRoot>
    </>
  );
};

interface RunDetailOverlaysProps {
  readonly runNumber: number;
  readonly status: RunStatus;
  readonly color: string;
  readonly durationMs: number | null;
  readonly startedBy: string;
  readonly onBack: () => void;
  /** Absent once the run can no longer be stopped. */
  readonly onStop?: () => void;
  readonly isStopping: boolean;
}

const RunDetailOverlays: React.FC<RunDetailOverlaysProps> = ({
  runNumber,
  status,
  color,
  durationMs,
  startedBy,
  onBack,
  onStop,
  isStopping,
}) => (
  <>
    <NavLegend />
    <ToolbarWrapper $position="top" data-testid="run-detail__toolbar">
      <Toolbar
        variant={TOOLBAR_VARIANT_FLOATING}
        density={TOOLBAR_DENSITY_COMPACT}
        dividerInset={8}
        ariaLabel="Run detail"
        dataTestId="run-detail-toolbar"
      >
        <Toolbar.Group ariaLabel="Navigation">
          <IconButton
            ariaLabel="Back to editor"
            tooltip="Back to process editor"
            tooltipPlacement={TOOLTIP_PLACEMENT_BOTTOM}
            size={ICON_BUTTON_SIZE_TB}
            icon={<MaterialIcon name="arrow_back" />}
            onClick={onBack}
            dataTestId="run-detail-toolbar__back"
          />
        </Toolbar.Group>
        <Toolbar.Group ariaLabel="Run info">
          <ModeBadge
            variant={MODE_BADGE_VARIANT_FLAT}
            tone={MODE_BADGE_TONE[status]}
            size={MODE_BADGE_SIZE_SM}
            dataTestId="run-detail-toolbar__badge"
          >
            Run #{runNumber}
          </ModeBadge>
          <FloatingStatusDot $color={color} />
          <FloatingStatusLabel $color={color}>
            {STATUS_LABEL[status]}
          </FloatingStatusLabel>
        </Toolbar.Group>
        {onStop && (
          <Toolbar.Group ariaLabel="Run actions">
            <IconButton
              ariaLabel="Stop this run"
              tooltip="Stop this run"
              tooltipPlacement={TOOLTIP_PLACEMENT_BOTTOM}
              size={ICON_BUTTON_SIZE_TB}
              icon={<MaterialIcon name="stop_circle" />}
              onClick={onStop}
              disabled={isStopping}
              dataTestId="run-detail-toolbar__stop"
            />
          </Toolbar.Group>
        )}
        {durationMs == null ? null : (
          <Toolbar.Group ariaLabel="Duration">
            <FloatingMeta>{formatDuration(durationMs)}</FloatingMeta>
          </Toolbar.Group>
        )}
        <Toolbar.Group ariaLabel="Started by">
          <FloatingMeta>{startedBy}</FloatingMeta>
        </Toolbar.Group>
      </Toolbar>
    </ToolbarWrapper>
  </>
);

export default RunDetailView;
